
import { Component, Vue, Watch } from 'vue-property-decorator';
import { SalesOrder, SalesOrderOrderStatusEnum } from '@/api/model';
import OrderCard from '@/components/orders/OrderCard.vue';
import Helpers from '@/logic/orders/helpers';
import OrderOverviewTable from '@/components/orders/OrderOverviewTable.vue';

@Component({
  components: { OrderOverviewTable, OrderCard },
})
export default class OrderOverview extends Vue {
  currentPage = 1;

  perPage = 3;

  loading = false;

  searchQuery = '';

  statusFilter = '';

  get orders(): SalesOrder[] {
    let orders = [...this.$store.getters['order/sortedOrders']];

    if (this.searchQuery) {
      // All fields that are full text searchable
      const textFields = [
        'OrderNumber',
        'PurchOrder',
        'City',
        'BillOfLading',
        'CreatedOn',
      ];
      orders = orders.filter((item: any) =>
        textFields.some((field: string) => {
          if (!item[field]) {
            return false;
          }
          const query = this.searchQuery.toLowerCase();
          const value =
            field !== 'CreatedOn'
              ? item[field].toLowerCase()
              : Helpers.getReadableCreatedDate(item);
          return value.includes(query);
        }),
      );
    }

    if (this.statusFilter) {
      orders = orders.filter(
        (item: any) => item.OrderStatus === this.statusFilter,
      );
    }

    return orders;
  }

  get hasOrders(): boolean {
    return this.$store.getters['order/hasOrders'];
  }

  get paginatedOrders(): SalesOrder[] {
    return this.orders.slice(
      (this.currentPage - 1) * this.perPage,
      this.currentPage * this.perPage,
    );
  }

  get statusOptions(): any {
    return {
      open: [
        SalesOrderOrderStatusEnum.AwsCreated,
        SalesOrderOrderStatusEnum.SapCreated,
        SalesOrderOrderStatusEnum.SapProcessing,
        SalesOrderOrderStatusEnum.SapProcessingPart,
      ],
      closed: [
        SalesOrderOrderStatusEnum.SapShipped,
        SalesOrderOrderStatusEnum.SapShippedPart,
        SalesOrderOrderStatusEnum.Delivered,
        SalesOrderOrderStatusEnum.SapCancelled,
        SalesOrderOrderStatusEnum.SapUnknown,
      ],
    };
  }

  async created() {
    try {
      this.loading = true;
      await this.$store.dispatch('order/fetchOrders');
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route', { immediate: true })
  routeChanged(newVal: any) {
    this.currentPage = parseInt(newVal.hash.replace(/#page/g, ''), 10) || 1;
  }

  filterStatus(group: 'open' | 'closed'): SalesOrder[] {
    const filters = this.statusOptions;
    return this.orders.filter(
      (order) =>
        order.OrderStatus && filters[group].includes(order.OrderStatus),
    );
  }

  getStatusLabel(status: SalesOrderOrderStatusEnum): string {
    const label = this.$t(`orders.status.${status}`);
    const showOnStatus = [
      SalesOrderOrderStatusEnum.SapProcessingPart,
      SalesOrderOrderStatusEnum.SapShippedPart,
    ];
    const partly = showOnStatus.includes(status)
      ? ` | ${this.$t('orders.status.partly')}`
      : '';
    return `${label}${partly}`;
  }
}
